import React, { useEffect } from 'react'
import { Text } from 'blaze-ui/Text/Text'
import Table from 'components/BlazeTable/BlazeTable'
import dayjs from 'dayjs'
import Person from 'common/PropertyValue/Types/Person'
import { PersonPropertyValue } from 'api/clients/PropertyClient'
import { connectHits, connectStateResults } from 'react-instantsearch-dom'
import { Empty } from 'screens/blaze/Search/SearchModal/components/SearchList/Empty/Empty'
import { Doc } from 'screens/BrandKits/components/BrandVoice/ContentSourcePicker/FromDoc'
import { isString } from 'lodash'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import Styles from './DocumentsTable.module.scss'
import cx from 'classnames'
import NoDocumentsState from './NoDocumentsState/NoDocumentsState'

interface Article {
  id: string
  title: string
  updated_at: string
  user: {
    display_name: string
    avatar_url: string
  }
}

const transformAlgoliaHit = (hit: any): Article => {
  // For some reason, the updated_at field is sometimes a string and sometimes a unix timestamp
  const updatedAt = isString(hit.updated_at) ? dayjs(hit.updated_at) : dayjs.unix(hit.updated_at)

  return {
    id: hit.key,
    title: hit.title,
    updated_at: updatedAt.toISOString(),
    user: {
      display_name: hit.owners[0].split('$')[0],
      avatar_url: `data:image/png;base64,${hit.owners[0].split('$')[1]}`,
    },
  }
}

interface TableProps {
  hits?: any[]
  onClick: (item: Doc) => void
  selectedDocumentId?: string
  searchState: {
    query: string
  }
  clearSearch: () => void
  onUploadPdf: () => void
  onInitialResults?: (results: { nbHits: number }) => void
}

const DocumentsTable: React.FC<TableProps> = ({
  hits,
  onClick,
  selectedDocumentId,
  clearSearch,
  searchState,
  onUploadPdf,
  onInitialResults,
}) => {
  const { isMobile } = useResponsiveDesign()
  const documents = hits?.map(transformAlgoliaHit)

  // Report initial document state only on mount or when hits change without a query
  useEffect(() => {
    if (onInitialResults && !searchState.query) {
      onInitialResults({ nbHits: documents?.length || 0 })
    }
  }, [documents?.length, onInitialResults, searchState.query])

  if (!documents?.length) {
    if (!searchState.query) {
      return <NoDocumentsState onUploadClick={onUploadPdf} />
    }
    return (
      <div className={Styles.emptyContainer}>
        <Empty query={searchState.query} onReset={clearSearch} />
      </div>
    )
  }

  return (
    <div className={cx(Styles.container)}>
      <Table>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Name</Table.Th>
            {!isMobile && <Table.Th maxWidth="150px">Last Modified</Table.Th>}
            {!isMobile && <Table.Th maxWidth="150px">Owner</Table.Th>}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {(documents || []).map((document) => (
            <Table.Tr
              key={document.id}
              onClick={() =>
                onClick({
                  id: document.id,
                  name: document.title,
                  emoji: null,
                })
              }
              selected={document.id === selectedDocumentId}
              selectable
            >
              <Table.Td>
                <Text variant="largeList">{document.title}</Text>
              </Table.Td>
              {!isMobile && <Table.Td maxWidth="150px">{dayjs(document.updated_at).format('MMM D, h:mm A')}</Table.Td>}
              {!isMobile && (
                <Table.Td maxWidth="150px">
                  <Person
                    propertyValue={
                      {
                        value: {
                          creator_name: document.user.display_name,
                          creator_image_url: document.user.avatar_url,
                        },
                      } as PersonPropertyValue
                    }
                  />
                </Table.Td>
              )}
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </div>
  )
}

export default DocumentsTable

export const ConnectedDocumentsTable: React.ComponentClass<any, TableProps> = connectStateResults(
  connectHits(DocumentsTable),
)
